import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { RefreshCw } from "lucide-react";
import {
  OverviewMetricsStrip,
  upcomingPeopleFromBoard,
} from "@/components/journey/overview/OverviewMetricsStrip";
import { OverviewTrendChart } from "@/components/journey/overview/OverviewTrendChart";
import { AdminAlert, AdminPageHeader } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { ApiError } from "@/lib/api";
import {
  fetchJourneyOverview,
  type JourneyOverviewPayload,
} from "@/lib/journey-overview";
import { windowLabel, type OverviewWindow } from "@/lib/journey-overview-format";
import { listJourneys, type JourneyDefinition } from "@/lib/journey-api";

const WINDOWS: { id: OverviewWindow; label: string }[] = [
  { id: "today", label: "今日" },
  { id: "7d", label: "近7天" },
  { id: "14d", label: "近14天" },
];

/** Journey 总览 · 两段数字 + 趋势曲线 */
export default function JourneyOverviewPage() {
  const { brandId } = useParams();
  const { accountId, brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);

  const [window, setWindow] = useState<OverviewWindow>("today");
  const [data, setData] = useState<JourneyOverviewPayload | null>(null);
  const [journeys, setJourneys] = useState<JourneyDefinition[]>([]);
  const [selectedJourneyIds, setSelectedJourneyIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const executionBase = `/accounts/${accountId}/brands/${brandId}/apps/journey/execution`;

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const [overview, journeyList] = await Promise.all([
        fetchJourneyOverview(accountId, { brandId, window }),
        listJourneys(accountId, brandId).catch(() => ({ journeys: [] as JourneyDefinition[] })),
      ]);
      setData(overview);
      setJourneys(journeyList.journeys ?? []);
    } catch (caught) {
      const message =
        caught instanceof ApiError ? caught.message : "加载总览失败，请稍后重试";
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, brandId, window]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="总览"
        description={
          brand ? `「${brand.name}」· ${windowLabel(window)}` : windowLabel(window)
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-border p-0.5">
              {WINDOWS.map((w) => (
                <Button
                  key={w.id}
                  type="button"
                  size="sm"
                  variant={window === w.id ? "default" : "ghost"}
                  className="h-8"
                  onClick={() => setWindow(w.id)}
                >
                  {w.label}
                </Button>
              ))}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => void load()}
            >
              <RefreshCw className={`mr-1 size-3.5 ${loading ? "animate-spin" : ""}`} />
              刷新
            </Button>
          </div>
        }
      />

      {error ? <AdminAlert>{error}</AdminAlert> : null}

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">加载作战数据…</p>
      ) : null}

      {data ? (
        <>
          <OverviewMetricsStrip
            metrics={data.metrics}
            window={window}
            executionBase={executionBase}
            upcomingPeople={upcomingPeopleFromBoard(data.board)}
          />

          <OverviewTrendChart
            series={data.series}
            chartFrom={data.chart_from}
            chartTo={data.chart_to}
            selectedJourneyIds={selectedJourneyIds}
          />

          {journeys.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>旅程叠加</span>
              {journeys.map((j) => {
                const on = selectedJourneyIds.includes(j.id);
                return (
                  <Button
                    key={j.id}
                    type="button"
                    size="sm"
                    variant={on ? "secondary" : "outline"}
                    className="h-7"
                    onClick={() =>
                      setSelectedJourneyIds((prev) =>
                        on ? prev.filter((id) => id !== j.id) : [...prev, j.id],
                      )
                    }
                  >
                    {j.name}
                  </Button>
                );
              })}
              {selectedJourneyIds.length > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7"
                  onClick={() => setSelectedJourneyIds([])}
                >
                  清除
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
